import React, { useState, useEffect, useCallback } from 'react';
import * as bugLogService from '../../services/adminBugLog.service';

/**
 * Bug Log — built 2026-09-02 after a real incident (Paula's blank-screen
 * bug) took far longer to resolve than it should have: a fix for the
 * exact same bug already existed from 2026-08-31, but nobody could find
 * it, and nobody knew it had never actually shipped. Jenn's framing:
 * "it's not about the bug, it's about how fast and how well we recover."
 *
 * Phase buttons (Start diagnosis / Mark deployed / Confirm resolved) stamp
 * timestamps with one click rather than asking anyone to type a date —
 * that's what actually makes the recovery-speed data real instead of a
 * field nobody fills in.
 */

const STATUS_COLOR = {
  open: 'bg-red-100 text-red-700',
  investigating: 'bg-amber-100 text-amber-700',
  resolved: 'bg-green-100 text-green-700',
};

const fmt = (iso) => iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }) : null;

const duration = (fromIso, toIso) => {
  if (!fromIso || !toIso) return null;
  const ms = new Date(toIso) - new Date(fromIso);
  if (ms < 0) return null;
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hrs < 24) return `${hrs}h ${remMins}m`;
  const days = Math.floor(hrs / 24);
  return `${days}d ${hrs % 24}h`;
};

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{label}</label>
      {children}
    </div>
  );
}

const inputCls = "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta";

function NewBugForm({ onCreated, onCancel }) {
  const [title, setTitle] = useState('');
  const [foundVia, setFoundVia] = useState('reported');
  const [reportedBy, setReportedBy] = useState('');
  const [presentedAs, setPresentedAs] = useState('');
  const [significance, setSignificance] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const bug = await bugLogService.createBug({
        title, foundVia, reportedBy, presentedAs, significance,
        reportedAt: foundVia === 'reported' ? new Date().toISOString() : null,
      });
      onCreated(bug);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create this entry.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-5 mb-4 space-y-3">
      <Field label="Title">
        <input className={inputCls} value={title} onChange={e => setTitle(e.target.value)} required autoFocus placeholder="Short label, e.g. Dashboard blank screen on empty sector data" />
      </Field>
      <Field label="How did this come up?">
        <div className="flex gap-2">
          <button type="button" onClick={() => setFoundVia('reported')}
            className={`text-xs font-semibold px-3 py-1.5 rounded-lg ${foundVia === 'reported' ? 'bg-reset-terracotta text-white' : 'bg-gray-100 text-gray-500'}`}>
            A member/staff reported it
          </button>
          <button type="button" onClick={() => setFoundVia('self_found')}
            className={`text-xs font-semibold px-3 py-1.5 rounded-lg ${foundVia === 'self_found' ? 'bg-reset-terracotta text-white' : 'bg-gray-100 text-gray-500'}`}>
            Found without a report
          </button>
        </div>
      </Field>
      {foundVia === 'reported' && (
        <Field label="Reported by">
          <input className={inputCls} value={reportedBy} onChange={e => setReportedBy(e.target.value)} placeholder="e.g. Paula via Jenn" />
        </Field>
      )}
      <Field label={foundVia === 'reported' ? 'How it presented to them' : 'What you found'}>
        <textarea className={inputCls} rows={2} value={presentedAs} onChange={e => setPresentedAs(e.target.value)} />
      </Field>
      <Field label="Significance">
        <textarea className={inputCls} rows={2} value={significance} onChange={e => setSignificance(e.target.value)} placeholder="Impact, in plain terms — who/what this affects" />
      </Field>
      {error && <div className="text-sm text-red-600">{error}</div>}
      <div className="flex gap-2">
        <button type="submit" disabled={saving} className="bg-reset-terracotta text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-50">
          {saving ? 'Saving…' : 'Log it'}
        </button>
        <button type="button" onClick={onCancel} className="text-sm text-gray-500 px-4 py-2">Cancel</button>
      </div>
    </form>
  );
}

function EditableTextarea({ value, onSave, placeholder, rows = 2 }) {
  const [draft, setDraft] = useState(value || '');
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setDraft(value || ''); setDirty(false); }, [value]);
  return (
    <div>
      <textarea
        className={inputCls} rows={rows} placeholder={placeholder}
        value={draft}
        onChange={e => { setDraft(e.target.value); setDirty(true); }}
      />
      {dirty && (
        <button onClick={() => { onSave(draft); setDirty(false); }} className="text-xs font-semibold text-reset-terracotta mt-1">Save</button>
      )}
    </div>
  );
}

function BugDetail({ bug, onChange }) {
  const [notes, setNotes] = useState([]);
  const [noteDraft, setNoteDraft] = useState('');
  const [loadingNotes, setLoadingNotes] = useState(true);

  const loadNotes = useCallback(async () => {
    setLoadingNotes(true);
    try { setNotes(await bugLogService.listNotes(bug.id)); } finally { setLoadingNotes(false); }
  }, [bug.id]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  const patch = async (fields) => {
    const updated = await bugLogService.updateBug(bug.id, fields);
    onChange(updated);
  };

  const submitNote = async (e) => {
    e.preventDefault();
    if (!noteDraft.trim()) return;
    await bugLogService.addNote(bug.id, noteDraft.trim());
    setNoteDraft('');
    loadNotes();
  };

  const nowIso = () => new Date().toISOString();

  return (
    <div className="px-5 pb-5 space-y-4 border-t border-gray-100 pt-4">
      {/* Phase buttons — one click stamps the timestamp for real */}
      <div className="flex flex-wrap gap-2">
        {!bug.diagnosis_started_at && (
          <button onClick={() => patch({ diagnosisStartedAt: nowIso(), status: 'investigating' })}
            className="text-xs font-semibold bg-amber-100 text-amber-700 px-3 py-1.5 rounded-lg">Start diagnosis</button>
        )}
        {!bug.fix_deployed_at && (
          <button onClick={() => patch({ fixDeployedAt: nowIso(), deployedToProduction: true })}
            className="text-xs font-semibold bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg">Mark deployed to production</button>
        )}
        {bug.deployed_to_production && !bug.verified_live && (
          <button onClick={() => patch({ verifiedLive: true })}
            className="text-xs font-semibold bg-purple-100 text-purple-700 px-3 py-1.5 rounded-lg">Mark verified live</button>
        )}
        {!bug.resolved_confirmed_at && (
          <button onClick={() => patch({ resolvedConfirmedAt: nowIso(), status: 'resolved' })}
            className="text-xs font-semibold bg-green-100 text-green-700 px-3 py-1.5 rounded-lg">Confirm resolved</button>
        )}
      </div>

      {/* Timing summary */}
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
        {bug.reported_at && <span>Reported {fmt(bug.reported_at)}</span>}
        {bug.diagnosis_started_at && <span>Diagnosis started {fmt(bug.diagnosis_started_at)}{bug.reported_at && ` (+${duration(bug.reported_at, bug.diagnosis_started_at)})`}</span>}
        {bug.fix_deployed_at && <span>Deployed {fmt(bug.fix_deployed_at)}{bug.diagnosis_started_at && ` (+${duration(bug.diagnosis_started_at, bug.fix_deployed_at)} diagnosing)`}</span>}
        {bug.resolved_confirmed_at && <span className="font-semibold text-reset-brown">Resolved {fmt(bug.resolved_confirmed_at)}{bug.reported_at && ` — ${duration(bug.reported_at, bug.resolved_confirmed_at)} total`}</span>}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Root cause"><EditableTextarea value={bug.root_cause} onSave={v => patch({ rootCause: v })} /></Field>
        <Field label="Category"><EditableTextarea rows={1} value={bug.root_cause_category} onSave={v => patch({ rootCauseCategory: v })} placeholder="e.g. deploy gap, data edge case, DNS/infra" /></Field>
        <Field label="Fix steps"><EditableTextarea value={bug.fix_steps} onSave={v => patch({ fixSteps: v })} /></Field>
        <Field label="Affected files"><EditableTextarea value={bug.affected_files} onSave={v => patch({ affectedFiles: v })} placeholder="Real file paths touched" /></Field>
        <Field label="Ruled out (dead ends)"><EditableTextarea value={bug.ruled_out} onSave={v => patch({ ruledOut: v })} placeholder="Theories tested and eliminated" /></Field>
        <Field label="Broke other things?"><EditableTextarea value={bug.broke_other_things} onSave={v => patch({ brokeOtherThings: v })} /></Field>
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Diagnosis trail</p>
        {loadingNotes ? (
          <p className="text-sm text-gray-400">Loading…</p>
        ) : notes.length === 0 ? (
          <p className="text-sm text-gray-400">No notes yet — add what you find as you go.</p>
        ) : (
          <div className="space-y-2 mb-3">
            {notes.map(n => (
              <div key={n.id} className="bg-reset-cream rounded-lg px-3 py-2 text-sm">
                <p className="text-reset-brown whitespace-pre-wrap">{n.note}</p>
                <p className="text-xs text-gray-400 mt-1">{n.author_name || 'Team365'} · {fmt(n.created_at)}</p>
              </div>
            ))}
          </div>
        )}
        <form onSubmit={submitNote} className="flex gap-2">
          <input className={inputCls} value={noteDraft} onChange={e => setNoteDraft(e.target.value)} placeholder="What did you just find?" />
          <button type="submit" className="text-xs font-semibold bg-reset-terracotta text-white px-3 py-2 rounded-lg shrink-0">Add</button>
        </form>
      </div>
    </div>
  );
}

export default function AdminBugLog({ embedded = false }) {
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setBugs(await bugLogService.listBugs({ q: q || undefined, status: status || undefined })); }
    finally { setLoading(false); }
  }, [q, status]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    setExporting(true);
    try { await bugLogService.downloadCsv({ q: q || undefined, status: status || undefined }); }
    finally { setExporting(false); }
  };

  const updateInList = (updated) => {
    setBugs(prev => prev.map(b => b.id === updated.id ? { ...b, ...updated } : b));
  };

  return (
    <div className={embedded ? '' : 'max-w-4xl mx-auto'}>
      {!embedded && <h1 className="font-serif text-2xl font-bold text-reset-brown mb-1">Bug Log</h1>}
      <p className="text-sm text-gray-500 mb-4">Every bug we find — reported or not — how we diagnosed it, and how fast we recovered.</p>

      <div className="flex flex-wrap gap-2 mb-4">
        <input
          className="flex-1 min-w-[180px] px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          placeholder="Search title, cause, fix, files…" value={q} onChange={e => setQ(e.target.value)}
        />
        <select value={status} onChange={e => setStatus(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="investigating">Investigating</option>
          <option value="resolved">Resolved</option>
        </select>
        <button onClick={handleExport} disabled={exporting} className="text-sm font-semibold bg-white border border-gray-200 text-reset-brown px-4 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-50">
          {exporting ? 'Exporting…' : '↓ Export CSV'}
        </button>
        <button onClick={() => setShowNew(v => !v)} className="text-sm font-semibold bg-reset-terracotta text-white px-4 py-2 rounded-lg">
          + Log a bug
        </button>
      </div>

      {showNew && <NewBugForm onCreated={(bug) => { setShowNew(false); setBugs(prev => [bug, ...prev]); setExpandedId(bug.id); }} onCancel={() => setShowNew(false)} />}

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : bugs.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm px-5 py-8 text-center text-sm text-gray-400">Nothing logged yet.</div>
      ) : (
        <div className="space-y-2">
          {bugs.map(bug => (
            <div key={bug.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
              <button onClick={() => setExpandedId(expandedId === bug.id ? null : bug.id)} className="w-full text-left px-5 py-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${STATUS_COLOR[bug.status]}`}>{bug.status}</span>
                    {bug.related_bug_title && <span className="text-[11px] text-gray-400">↻ recurrence of "{bug.related_bug_title}"</span>}
                  </div>
                  <p className="text-sm font-semibold text-reset-brown truncate">{bug.title}</p>
                  <p className="text-xs text-gray-400">{bug.found_via === 'reported' ? `Reported${bug.reported_by ? ` by ${bug.reported_by}` : ''}` : 'Found without a report'} · {fmt(bug.created_at)}{bug.note_count > 0 && ` · ${bug.note_count} note${bug.note_count === 1 ? '' : 's'}`}</p>
                </div>
                <span className="text-gray-300 text-lg shrink-0">{expandedId === bug.id ? '−' : '+'}</span>
              </button>
              {expandedId === bug.id && <BugDetail bug={bug} onChange={updateInList} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
